"use client"
//to setup the process.env variables, create a .env file in the root of your project with the following content:

// CARD TO ADD THE TASK
import { useState, useEffect, useRef } from "react"
import { ChevronDown, X, File } from "lucide-react"
import { useAuth } from "../Contexts/AuthContext"
import TokenChecker, { API_BASE_URL } from "./TokenChecker"
import { warn } from "../utils/logger";

const AddTaskCard = ({
  onAddTask,
  onCancel,
  isShared = false,
  buttonLabel,
  category,
  initialData = {},
  task_id,
  folderId,
  forceFolderView = false,
}) => {
  const [title, setTitle] = useState(initialData.title || "")
  const [description, setDescription] = useState(initialData.description || "")
  const [dueDate, setDueDate] = useState(initialData.dueDate || "")
  const [priority, setPriority] = useState(initialData.priority || "Choose Priority")
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [trustedUsers, setTrustedUsers] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const suggestBoxRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [file, setFile] = useState(null)
  
  // New states for attachment management
  const [existingAttachment, setExistingAttachment] = useState(null)
  const [attachmentToRemove, setAttachmentToRemove] = useState(false)

  const { authData } = useAuth()
  const userEmail = authData.email
  const isEditMode = !!initialData.id
  const isSharedEdit = isEditMode && isShared

  // Initialize existing attachment data
  useEffect(() => {
    if (isEditMode && (initialData.attachmentKey || initialData.attachmentUrl)) {
      setExistingAttachment({
        key: initialData.attachmentKey,
        url: initialData.attachmentUrl,
        // Extract filename from key or use a default
        name: initialData.attachmentKey ? initialData.attachmentKey.split('/').pop() : 'Existing File'
      })
    }
  }, [isEditMode, initialData])

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(""), 5000)
      return () => clearTimeout(timeout)
    }
  }, [error])

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(""), 5000)
      return () => clearTimeout(timeout)
    }
  }, [success])

  // Fetch my trusted users once when adding a shared task
  useEffect(() => {
    const fetchTrusted = async () => {
      try {
        if (!(isShared && !isEditMode)) return
        const res = await TokenChecker('/api/settings/trusted-users/', { method: 'GET' })
        const data = await res.json()
        if (res.ok && Array.isArray(data)) {
          setTrustedUsers(data)
        }
      } catch (e) {
        // Non-blocking; silently ignore
      }
    }
    fetchTrusted()
  }, [isShared, isEditMode])

  // Filter suggestions when the email input changes
  useEffect(() => {
    if (!isShared || isEditMode) return
    const q = (email || '').trim().toLowerCase()
    if (!q) {
      setSuggestions([])
      setShowSuggestions(false)
      setHighlightIndex(-1)
      return
    }
    const results = trustedUsers
      .map(rel => rel?.trusted)
      .filter(Boolean)
      .filter(u => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase()
        const em = (u.email || '').toLowerCase()
        return name.includes(q) || em.includes(q)
      })
      .slice(0, 6)

    setSuggestions(results)
    setShowSuggestions(results.length > 0)
    setHighlightIndex(results.length ? 0 : -1)
  }, [email, isShared, isEditMode, trustedUsers])

  // Close suggestions on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    if (showSuggestions) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSuggestions])

  async function uploadToS3(file, token) {
    const res = await fetch(`${API_BASE_URL}/api/uploads/presign/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
      }),
    })

    const { url, key, contentType, headers: requiredHeaders } = await res.json()

    // Use required headers from backend (includes Content-Type and optional SSE)
    const putHeaders = requiredHeaders && typeof requiredHeaders === 'object'
      ? requiredHeaders
      : { "Content-Type": contentType }

    const uploadRes = await fetch(url, {
      method: "PUT",
      headers: putHeaders,
      body: file,
    })

    if (!uploadRes.ok) {
      try {
        const errText = await uploadRes.text();
        warn("S3 upload failed:", uploadRes.status, errText);
      } catch(_) {}
      throw new Error("Upload failed")
    }

    // no-op logging
    const encodedKey = encodeURIComponent(key);
    const bucket = process.env.REACT_APP_S3_BUCKET || process.env.REACT_APP_AWS_S3_BUCKET;
    const region = process.env.REACT_APP_S3_REGION || process.env.REACT_APP_AWS_DEFAULT_REGION || 'us-east-1';
    const publicUrl = bucket ? `https://${bucket}.s3.${region}.amazonaws.com/${encodedKey}` : '';
    return {key, publicUrl}
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!title || !dueDate || priority === "Choose Priority") {
      alert("Please fill out all required fields, including the PRIORITY.")
      return
    }

    // For shared tasks, ensure we have a plausible email
    if (isShared && !isEditMode) {
      const val = (email || '').trim()
      const looksLikeEmail = /.+@.+\..+/.test(val)
      if (!looksLikeEmail) {
        setError("Enter a valid email or select a trusted user from suggestions.")
        return
      }
    }

    let attachmentKey = null
    let attachmentUrl = null
    
    // Handle attachment logic
    if (attachmentToRemove) {
      // User wants to remove the existing attachment
      attachmentKey = null
      attachmentUrl = null
    } else if (file) {
      // User is uploading a new file
      try {
        const result = await uploadToS3(file, authData.accessToken)
        attachmentKey = result.key
        attachmentUrl = result.publicUrl
      } catch (err) {
        setError("Failed to upload file. Please try again.")
        setIsLoading(false)
        return
      }
    } else if (existingAttachment && !attachmentToRemove) {
      // Keep existing attachment
      attachmentKey = existingAttachment.key
      attachmentUrl = existingAttachment.url
    }

    const task = {
      id: task_id,
      title,
      description,
      dueDate,
      priority,
      owner: userEmail,
      category,
      attachmentKey: attachmentKey, // ✅ NEW: Add attachment key to task
      attachmentUrl: attachmentUrl, // ✅ NEW: Add attachment URL to task
    }

    if (forceFolderView && folderId) {
      task.folder_id = folderId
    }

    if (isShared && email) task.email = email.trim()

    let endpoint = ""
    if (isSharedEdit) {
      endpoint = `/edit_shared_task/`
    } else if (forceFolderView && folderId) {
      endpoint = "/create_list"
    } else if (category === "Personal Tasks") {
      endpoint = "/create_list"
    } else if (category === "Tasks Delegated - Assigned") {
      endpoint = "/send_email/assign_user/"
    } else if (category === "Tasks Delegated - Invitation") {
      endpoint = "/send_email/invite_user"
    }

    try {
      setIsLoading(true)

      const response = await TokenChecker(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(task),
      })

      const data = await response.json()

      if (response.ok) {
        // If backend invited a non-user to join Flowman, surface message and stop.
        if (data && data.invitation_sent) {
          setSuccess(data.message || "User not found invitation sent to join flowman")
          setError("")
          return
        }

        const createdTaskId = data.task_id || data.id
        onAddTask({ ...task, id: createdTaskId, folder: folderId ? { id: folderId } : null })
        setError("")
      } else {
        setError(data.error || "Failed to add task. Please try again.")
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const priorityColors = {
    Immediate: "border-red-500",
    Moderate: "border-yellow-500",
    Lenient: "border-green-500",
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setAttachmentToRemove(false) // Reset removal flag when new file is selected
    }
  }

  const removeFile = () => {
    setFile(null)
  }

  const removeExistingAttachment = () => {
    setAttachmentToRemove(true)
    setExistingAttachment(null)
    setFile(null) // Also clear any new file selection
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  return (
    <>
      {success && (
        <div className="mx-4 mb-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4 shadow-md flex justify-between items-start">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
            <div className="text-sm text-blue-700 font-medium">{success}</div>
          </div>
          <button className="ml-4 text-blue-600 hover:text-blue-800 focus:outline-none" onClick={() => setSuccess("")}>
            ✕
          </button>
        </div>
      )}
      {error && (
        <div className="mx-4 mb-4 rounded-lg border-l-4 border-[#E53E3E] bg-red-50 p-4 shadow-md flex justify-between items-start">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-[#E53E3E] mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-.01-8a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-[#C53030] font-medium">{error}</div>
          </div>
          <button className="ml-4 text-[#C53030] hover:text-[#9B2C2C] focus:outline-none" onClick={() => setError("")}>
            ✕
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={`mb-2 border-l-4 ${priorityColors[priority] || "border-[#4299E1]"} bg-white rounded-lg shadow-md`}
      >
        <div className="p-4 space-y-6">
          <div className="flex justify-between items-start mb-1">
            <input
              type="text"
              placeholder="Task Title"
              className="text-lg font-semibold text-[#2D3748] w-full border-none focus:outline-none focus:ring-0"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <div className="flex items-center w-fit">
              <div className="relative">
                <button
                  type="button"
                  className={`flex items-center justify-between w-40 px-3 py-1 text-sm border rounded-md ${priorityColors[priority]} text-[#4A5568]`}
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {priority}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </button>
                {isOpen && (
                  <div className="absolute z-10 w-32 mt-1 bg-white border rounded-md shadow-lg">
                    {Object.keys(priorityColors).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`block w-full text-left px-3 py-2 text-sm ${p === priority ? "bg-gray-100" : "hover:bg-gray-50"}`}
                        onClick={() => {
                          setPriority(p)
                          setIsOpen(false)
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <textarea
            placeholder="Task description"
            className="w-full px-3 py-2 text-sm text-[#4A5568] border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1] mb-4"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <input
                  type="date"
                  className="text-sm text-[#718096] border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                />

                <div className="relative">
                  <input id="file-input" type="file" className="hidden" onChange={handleFileChange} />
                  <button
                    type="button"
                    onClick={() => document.getElementById("file-input").click()}
                    className="flex items-center space-x-1 px-3 py-2 text-md text-[#1c1c1c] hover:text-[#0e93ff] rounded-md hover:bg-[#EBF8FF] transition-colors duration-200 opacity-70 hover:opacity-100"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    <span>{(isEditMode && initialData.attachmentKey) ? "Replace" : "Attach"}</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {isShared && !isEditMode && (
                  <div className="relative" ref={suggestBoxRef}>
                    <input
                      type="text"
                      className="text-sm text-[#718096] border border-gray-300 h-10 w-72 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
                      value={email}
                      placeholder="Type a name or email"
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => {
                        if (suggestions.length) setShowSuggestions(true)
                      }}
                      onKeyDown={(e) => {
                        if (!showSuggestions || !suggestions.length) return
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          setHighlightIndex(i => (i + 1) % suggestions.length)
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault()
                          setHighlightIndex(i => (i - 1 + suggestions.length) % suggestions.length)
                        } else if (e.key === 'Enter') {
                          if (highlightIndex >= 0) {
                            e.preventDefault()
                            const u = suggestions[highlightIndex]
                            setEmail(u.email)
                            setShowSuggestions(false)
                          }
                        } else if (e.key === 'Escape') {
                          setShowSuggestions(false)
                        }
                      }}
                      required
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-[18rem] bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                        {suggestions.map((u, idx) => {
                          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
                          const active = idx === highlightIndex
                          return (
                            <button
                              type="button"
                              key={u.id || u.email}
                              className={`w-full text-left px-3 py-2 text-sm ${active ? 'bg-[#EBF8FF]' : 'hover:bg-gray-50'}`}
                              onMouseEnter={() => setHighlightIndex(idx)}
                              onClick={() => {
                                setEmail(u.email)
                                setShowSuggestions(false)
                              }}
                            >
                              <div className="text-[#2D3748] font-medium truncate">{name}</div>
                              <div className="text-[#718096] text-xs truncate">{u.email}</div>
                            </button>
                          )
                        })}
                        <div className="px-3 py-2 text-xs text-gray-400 border-t">Trusted users</div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="px-4 py-2 rounded-md font-medium bg-[#4299E1] text-white hover:bg-[#3182CE] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4299E1]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    buttonLabel
                  )}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md font-medium border border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4299E1]"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Existing Attachment Display */}
            {existingAttachment && !attachmentToRemove && !file && (
              <div className="flex items-center justify-between p-3 bg-[#F0F9FF] border border-[#4299E1] rounded-md">
                <div className="flex items-center space-x-2">
                  <File className="w-4 h-4 text-[#4299E1]" />
                  <span className="text-sm text-[#2D3748] truncate max-w-xs">
                    {existingAttachment.name} (existing)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeExistingAttachment}
                  className="text-[#718096] hover:text-[#E53E3E] transition-colors duration-200"
                  title="Remove existing attachment"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* New File Display */}
            {file && (
              <div className="flex items-center justify-between p-3 bg-[#EBF8FF] border border-[#4299E1] rounded-md">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-[#4299E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-sm text-[#2D3748] truncate max-w-xs">{file.name}</span>
                  <span className="text-xs text-[#718096]">({formatFileSize(file.size)})</span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-[#718096] hover:text-[#E53E3E] transition-colors duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Removal Confirmation Message */}
            {attachmentToRemove && !file && (
              <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-sm text-red-700">Existing attachment will be removed</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAttachmentToRemove(false)
                    if (initialData.attachmentKey || initialData.attachmentUrl) {
                      setExistingAttachment({
                        key: initialData.attachmentKey,
                        url: initialData.attachmentUrl,
                        name: initialData.attachmentKey ? initialData.attachmentKey.split('/').pop() : 'Existing File'
                      })
                    }
                  }}
                  className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  title="Undo removal"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  )
}

export default AddTaskCard
